import React, { createContext, useState, useCallback, useRef, ReactNode } from 'react';
import type { BallotRef } from '@/services/chatRequest';

export interface SendChatOptions {
  /** Force one turn's answer depth, overriding the session selector. */
  depth?: string;
  /** The ballot row this message was sent from (Full profile / Explain / Compare). */
  ballotRef?: BallotRef;
}

export type HandleSendFunction = (message: string, options?: SendChatOptions) => Promise<void>;

interface ChatContextValue {
  sendChat: HandleSendFunction;
  registerChatHandler: (handler: HandleSendFunction) => void;
  unregisterChatHandler: () => void;
  isProcessing: boolean;
  setIsProcessing: (isProcessing: boolean) => void;
}

export const ChatContext = createContext<ChatContextValue | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const sendChatRef = useRef<HandleSendFunction | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const registerChatHandler = useCallback((handler: HandleSendFunction) => {
    sendChatRef.current = handler;
  }, []);

  const unregisterChatHandler = useCallback(() => {
    sendChatRef.current = null;
  }, []);

  // Wrap the ref in a stable callback that always calls the current handler
  const sendChat = useCallback<HandleSendFunction>(async (message, options) => {
    if (sendChatRef.current) {
      return sendChatRef.current(message, options);
    }
  }, []);

  const value: ChatContextValue = {
    sendChat,
    registerChatHandler,
    unregisterChatHandler,
    isProcessing,
    setIsProcessing,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
